import {
  getFreeDiskSpace,
  getCachedVideosSize,
  clearAllCachedVideos,
} from '@/services/videoDownload';

/** Headroom required on top of the file size before we allow a download. */
export const STORAGE_BUFFER_BYTES = 200 * 1024 * 1024; // 200 MB

export type SpaceCheckResult = { status: 'ok' | 'insufficient' };

/**
 * Barrier 1 — proactive disk-space check.
 *
 * Ensures there's room for a video of `fileBytes` plus a safety buffer. If free
 * space is short, attempts to free room by clearing other cached videos and
 * re-checks. Returns 'insufficient' only when, even after eviction, there still
 * isn't enough room.
 *
 * Skips the check (returns 'ok') when free space can't be determined — e.g. on
 * web, where a full cache degrades to streaming, or when the file size is
 * unknown. In those cases the actual write failing is the safety net (barrier 2).
 */
export async function ensureSpaceForVideo(
  fileBytes: number,
): Promise<SpaceCheckResult> {
  const free = await getFreeDiskSpace();
  if (free == null) return { status: 'ok' }; // web / unsupported → skip
  if (fileBytes <= 0) return { status: 'ok' }; // unknown size → rely on barrier 2

  const required = fileBytes + STORAGE_BUFFER_BYTES;
  if (free >= required) return { status: 'ok' };

  // Would evicting other cached videos free enough room?
  const cached = await getCachedVideosSize();
  if (free + cached < required) return { status: 'insufficient' };

  // Safe to clear all: the video we're about to download is never cached at
  // this point (cache hits short-circuit upstream before the space check).
  await clearAllCachedVideos();
  const freeAfter = await getFreeDiskSpace();
  return freeAfter != null && freeAfter >= required
    ? { status: 'ok' }
    : { status: 'insufficient' };
}

/**
 * Barrier 2 — best-effort classification of a failed write as out-of-space.
 *
 * Covers both platforms: web surfaces a typed `QuotaExceededError`, while native
 * file-system errors only carry a message string (e.g. `ENOSPC`).
 */
export function isOutOfSpaceError(err: unknown): boolean {
  const e = err as { name?: string; message?: string };
  if (e?.name === 'QuotaExceededError') return true; // web
  return /ENOSPC|no space|not enough space|disk (is )?full/i.test(
    e?.message ?? '',
  ); // native
}
